import { useMutation, useQueryClient } from "@tanstack/react-query"
import { addProductVariant } from "@/lib/api/product.api"
import type { VariantFormValues } from "@/lib/schemas/product.schema"
import { useAuthStore } from "@/store/auth.store"

export function useAddVariant(productId: string) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: VariantFormValues) => {
      if (!accessToken) throw new Error("Not authenticated")

      const measureUnit = values.unit || values.measureUnit

      // Strip undefined or empty fields
      const payload = Object.fromEntries(
        Object.entries({
          sku: values.sku,
          name: values.name,
          description: values.description,
          tags: values.tags,
          hsnCode: values.hsnCode,
          price: values.price,
          currencyCode: values.currencyCode,
          stockQuantity: values.stockQuantity,
          measureUnit: measureUnit || undefined,
        }).filter(([, v]) => v !== undefined)
      )

      return addProductVariant(productId, payload as unknown as Parameters<typeof addProductVariant>[1], accessToken)
    },
    onSuccess: () => {
      // Invalidate the specific product details cache so it re-fetches
      queryClient.invalidateQueries({ queryKey: ["product", productId] })
    },
    onError: (error: Error) => {
      console.error("Variant creation failed:", error.message)
    },
  })
}
